import { h } from 'vue';
import { useDialogStore, useStore } from '@/store';
import { MaybeComputedRef, useStorage } from '@vueuse/core';

import {
  DynamicDialogCloseOptions,
  DynamicDialogInstance,
  DynamicDialogOptions,
} from 'primevue/dynamicdialogoptions';
import { TImportTokenDialogMode, TImportTokenDialogStore } from '@/types';

import { NoLpDialog } from '@/components/Dialogs/NoLpDialog';
import { InstallMobileAppDialog } from '@/components/Dialogs/InstallMobileAppDialog';
import { SelectTokenDialog } from '@/components/Dialogs/SelectTokenDialog';
import { ConnectWalletDialog } from '@/components/Dialogs/ConnectWalletDialog';
import { DialogHeader } from '@/components/UI/DialogHeader';
import { ImportTokenDialog } from '@/components/Dialogs/ImportTokenDialog';
import { SwapConfirmDialog } from '@/components/Dialogs/SwapConfirmDialog';
import { PriceImpactWarningDialog } from '@/components/Dialogs/PriceImpactWarningDialog.vue';
import { PoolMobileDialog, PoolDialogHeader } from '@/components/Dialogs/Pools';

import { SendAnyTransactionDialog } from '@/components/Dialogs/SendAnyTransactionDialog';
import { RequestTokensDialog } from '@/components/Dialogs/RequestTokensDialog';
import { UnverifiedTokenDialog } from '@/components/Dialogs/UnverifiedTokenDialog';
import { L2WelcomeDialog } from '@/components/Dialogs/L2WelcomeDialog';
import { ITablePool, ITablePoolV1 } from '@/types/pools';
import { BurnLiquidityDialog } from '@/components/Dialogs/Steps';

export type TDialog = {
  open: (content: any, options?: DynamicDialogOptions) => DynamicDialogInstance;
};

export type TOnCloseCb = (
  options?: DynamicDialogCloseOptions | undefined,
) => void;

const store = useStore();
const dialogStore = useDialogStore();

export const showConnectWalletDialog = async (dialog: TDialog) => {
  dialog.open(ConnectWalletDialog, {
    props: {
      modal: true,
      style: store.isMobile.value
        ? {
            width: '100%',
            marginTop: '0px',
            height: '100vh',
            maxHeight: '100%', // as in the p-maximizable class
            margin: '0px',
          }
        : {
            width: '483px',
            marginTop: '91px',
            height: 'auto',
            maxHeight: 'auto',
          },
      contentStyle: {
        width: '100%',
        overflowY: 'hidden',
        height: store.isMobile.value ? '100vh' : 'auto',
      },
      position: 'top',
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: 'Connect a Wallet',
        }),
    },
  });
};

/**
 * ImportTokenDialog
 */
type TImportTokenDialogData = {
  mode?: TImportTokenDialogMode;
  store?: TImportTokenDialogStore;
  token: string;
  onImported?: MaybeComputedRef<boolean>;
};

export const showImportTokenDialog = (
  dialog: TDialog,
  data: TImportTokenDialogData,
  closeCb?: TOnCloseCb,
) => {
  dialog.open(ImportTokenDialog, {
    data: {
      mode: data.mode,
      store: data.store,
      token: data.token,
      onImported: data.onImported,
    },
    props: {
      modal: true,
      style: {
        maxWidth: '480px',
        width: '100%',
        maxHeight: '700px',
        height: '80vh',
      },
      contentStyle: { overflowY: 'scroll', maxWidth: '480px', width: '100%' },
      contentClass: 'import-token-dialog_without-scrollbar',
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: 'Import Token',
          hasBack: false,
        }),
    },
    onClose: (options) => {
      if (!closeCb) return;
      closeCb({
        data: {
          mode: data.mode,
          store: data.store,
          isCloseClicked: options?.data?.isCloseClicked,
          isImportClicked: options?.data?.isImportClicked,
        },
        type: 'dialog-close',
      });
    },
  });
};

/**
 * NoLpDialog
 */
export const showNoLpDialog = (dialog: TDialog) => {
  dialog.open(NoLpDialog, {
    props: {
      modal: true,
      draggable: false,
      closable: true,
      showHeader: true,
      dismissableMask: true,
      contentClass: 'stepped-dialog stepped-dialog--panel',
      style: 'margin: 0px 12px',
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: '',
          hasCloseButton: false,
        }),
    },
  });
};

/**
 * SelectTokenDialog
 */
export type TSelectTokenDialogData = {
  actionToken?: MaybeComputedRef<string | undefined> | string;
  secondaryToken?: MaybeComputedRef<string | undefined> | string;
  dialogType: 'add-liquidity' | 'burn-liquidity' | 'create-pool' | 'swap';
  field?: 'to' | 'from' | 'none';
  direction?: 'to' | 'from';
  mode?: 'token';
  burnType?: 'first' | 'second';
};

type TAppendTo = HTMLElement | 'body' | 'self';

export const showSelectTokenDialog = (
  dialog: TDialog,
  data: TSelectTokenDialogData,
  props?: {
    appendTo?: TAppendTo;
  },
) => {
  dialogStore.setDialogTitle('Select Tokens');
  dialogStore.setHasBack(false);
  dialog.open(SelectTokenDialog, {
    data: {
      actionToken: data.actionToken,
      secondaryToken: data.secondaryToken,
      dialogType: data.dialogType,
      field: data.field,
      direction: data.direction,
      mode: data.mode,
      burnType: data.burnType,
    },
    props: {
      modal: true,
      style: { width: '480px', maxHeight: '700px', height: '80vh' },
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
      contentClass: 'stepped-dialog stepped-dialog--panel',
      contentStyle: { height: '100%' },
      appendTo: props?.appendTo || 'body',
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: dialogStore.dialogTitle,
          hasBack: dialogStore.hasBack,
          onBack: dialogStore.onBack,
          hasCloseButton: false,
        }),
    },
    onClose() {
      dialogStore.resetDialogState();
    },
  });
};

type PoolAction = {
  type: 'add-liquidity' | 'burn-liquidity' | 'swap';
  pool: ITablePool | ITablePoolV1;
};

export type PoolCbOptions = Omit<DynamicDialogCloseOptions, 'data'> & {
  data: PoolAction;
};

export const showPoolDetailsDialog = (
  dialog: TDialog,
  data: {
    pool: ITablePool | ITablePoolV1;
    isPoolV1?: boolean;
  },
  closeCb: (options: PoolCbOptions) => void,
) => {
  dialog.open(PoolMobileDialog, {
    data: { pool: data.pool },
    props: {
      modal: true,
      draggable: false,
      closable: true,
      dismissableMask: true,
      contentStyle: 'padding: 0',
    },
    templates: {
      header: () =>
        h(PoolDialogHeader, {
          title: '',
          pool: data.pool,
        }),
    },
    onClose: (options: PoolCbOptions) => closeCb(options),
  });
};

/**
 * InstallMobileAppDialog
 */
export const installMobileAppDialog = useStorage(
  'install-mobile-app-dialog',
  { periodTimestamp: 0 },
  window.localStorage,
  { writeDefaults: true },
);

const TwoDayInMs = 172_800_000;
export const isTimeToShowInstallMobileAppDialog = () =>
  Date.now() > installMobileAppDialog.value.periodTimestamp;

export const handleInstallMobileAppDialog = () => {
  if (!isTimeToShowInstallMobileAppDialog()) return;

  installMobileAppDialog.value.periodTimestamp = Date.now() + TwoDayInMs;
};

export const showInstallMobileAppDialog = (
  dialog: TDialog,
  data?: { type: 'ios' | 'android' },
) => {
  dialog.open(InstallMobileAppDialog, {
    data: {
      type: data?.type,
    },
    props: {
      modal: true,
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
      contentStyle: {
        padding: '0 16px 16px',
      },
      position: 'bottom',
      style: {
        'margin-bottom': '80px',
      },
      breakpoints: {
        '767px': '50%',
        '500px': '100%',
      },
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: '',
          hasBack: false,
          hasCloseButton: false,
        }),
    },
  });
};

export const showSwapConfirmDialog = (
  dialog: TDialog,
  onClose?: TOnCloseCb,
) => {
  dialogStore.setDialogTitle('Swap Tokens');
  dialog.open(SwapConfirmDialog, {
    props: {
      modal: true,
      contentStyle: { width: '400px' },
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: dialogStore.dialogTitle,
          hasBack: dialogStore.hasBack,
          onBack: dialogStore.onBack,
        }),
    },
    onClose() {
      dialogStore.resetDialogState();
      onClose && onClose();
    },
  });
};

export const showBurnDialog = (dialog: TDialog, onCloseCb: TOnCloseCb) => {
  dialogStore.setDialogTitle('Remove Liquidity');
  dialogStore.setHasBack(false);
  dialog.open(BurnLiquidityDialog, {
    props: {
      modal: true,
      contentStyle: { width: '400px' },
      draggable: false,
      closable: false,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: dialogStore.dialogTitle,
          hasBack: dialogStore.hasBack,
          onBack: dialogStore.onBack,
          hasCloseButton: true,
        }),
    },
    async onClose(options) {
      onCloseCb && onCloseCb(options);
    },
  });
};

export const showPriceImpactWarningDialog = (
  dialog: TDialog,
  data: { priceImpact: number | undefined },
  onClose: TOnCloseCb,
) => {
  dialog.open(PriceImpactWarningDialog, {
    data,
    props: {
      modal: true,
      draggable: false,
      closable: true,
      dismissableMask: true,
      contentStyle: {
        padding: '0 32px 32px',
      },
      breakpoints: { '758px': 'width:100%' },
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: '',
        }),
    },
    onClose,
  });
};

export const showInstallMobileAppDialogByOS = (dialog: TDialog) => {
  /*
  if (detectIos()) {
    showInstallMobileAppDialog(dialog, { type: 'ios' });
    return;
  }
  */

  //TODO: Uncomment after the release of the android app
  // if (detectAndroid()) {
  //   showInstallMobileAppDialog(dialog, { type: 'android' });
  //   return;
  // }

  showConnectWalletDialog(dialog);
};

export const showSendAnyTransactionDialog = (
  dialog: TDialog,
  data: any,
  onClose?: TOnCloseCb,
) => {
  dialogStore.setDialogTitle(data?.title);
  dialog.open(SendAnyTransactionDialog, {
    data: data,
    props: {
      modal: true,
      contentStyle: { width: '400px' },
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: dialogStore.dialogTitle,
          hasBack: dialogStore.hasBack,
          onBack: dialogStore.onBack,
        }),
    },
    onClose(options) {
      if (onClose) {
        onClose(options);
      }
      dialogStore.resetDialogState();
    },
  });
};

export const showRequestTokenDialog = (dialog: TDialog) => {
  dialog.open(RequestTokensDialog, {
    props: {
      modal: true,
      contentStyle: { width: '400px' },
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: '',
          hasBack: dialogStore.hasBack,
          onBack: dialogStore.onBack,
        }),
    },
    onClose() {
      dialogStore.resetDialogState();
    },
  });
};

export const showUnverifiedTokenDialog = (
  dialog: TDialog,
  closeCb?: TOnCloseCb,
) => {
  dialog.open(UnverifiedTokenDialog, {
    props: {
      modal: true,
      draggable: false,
      showHeader: dialogStore.showHeader,
      closable: true,
      dismissableMask: true,
      contentClass: 'stepped-dialog stepped-dialog--panel',
      style: { maxWidth: '483px' },
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          class: 'stepped-header',
          title: '',
          hasBack: dialogStore.hasBack,
          onBack: dialogStore.onBack,
        }),
    },
    onClose: (options) => {
      if (!closeCb) return;
      return closeCb(options);
    },
  });
};

export const showWelcomeL2Dialog = (dialog: TDialog, closeCb?: TOnCloseCb) => {
  dialog.open(L2WelcomeDialog, {
    props: {
      modal: true,
      draggable: false,
      showHeader: dialogStore.showHeader,
      closable: true,
      dismissableMask: true,
      contentClass: 'stepped-dialog stepped-dialog--panel',
      style: { maxWidth: '483px' },
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: '',
        }),
    },
    onClose: (options) => {
      if (!closeCb) return;
      return closeCb(options);
    },
  });
};
